import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../services/api';
import ProjectForm from './ProjectForm';
import ProjectDetail from './ProjectDetail';
import './ProjectsTab.css';

const ProjectsTab = ({ houseId, initialProjectId, onProjectViewed }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    if (houseId !== null) {
      loadProjects();
    } else {
      setProjects([]);
      setLoading(false);
    }
  }, [houseId, filterStatus]);

  // Open project when navigated from dashboard
  useEffect(() => {
    if (initialProjectId && projects.length > 0) {
      const project = projects.find((p) => p.id === initialProjectId);
      if (project) {
        setSelectedProject(project);
        if (onProjectViewed) onProjectViewed();
      }
    }
  }, [initialProjectId, projects]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll(houseId, filterStatus || undefined);
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    setSelectedProject(null);
    setShowForm(true);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setShowForm(true);
  };

  const handleViewProject = (project) => {
    setSelectedProject(project);
    setShowForm(false);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedProject(null);
    loadProjects();
  };

  const handleCloseDetail = () => {
    setSelectedProject(null);
    loadProjects();
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project? This will also delete all associated files and expenses.')) {
      return;
    }

    try {
      await projectsAPI.delete(id);
      loadProjects();
      if (selectedProject && selectedProject.id === id) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const getStatusColor = (status) => {
    // Handle both string and enum values
    const statusStr = typeof status === 'string' ? status : (status?.value || status);
    const colors = {
      planned: 'var(--info)',
      in_progress: 'var(--primary)',
      on_hold: 'var(--warning)',
      completed: 'var(--success)',
      cancelled: 'var(--error)',
    };
    return colors[statusStr] || 'var(--text-secondary)';
  };

  const getPriorityColor = (priority) => {
    // Handle both string and enum values
    const priorityStr = typeof priority === 'string' ? priority : (priority?.value || priority);
    const colors = {
      low: 'var(--info)',
      medium: 'var(--primary)',
      high: 'var(--warning)',
      urgent: 'var(--error)',
    };
    return colors[priorityStr] || 'var(--text-secondary)';
  };

  const getStatusDisplay = (status) => {
    const statusStr = typeof status === 'string' ? status : (status?.value || status || 'planned');
    return statusStr.replace('_', ' ');
  };

  const getPriorityDisplay = (priority) => {
    const priorityStr = typeof priority === 'string' ? priority : (priority?.value || priority || 'medium');
    return priorityStr;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const calculateBudgetProgress = (estimated, actual) => {
    if (!estimated || estimated === 0) return 0;
    return Math.min((actual / estimated) * 100, 100);
  };

  if (!houseId) {
    return <div className="projects-message">Please select a house</div>;
  }

  if (loading && projects.length === 0) {
    return <div className="projects-loading">Loading projects...</div>;
  }

  // Show project detail view
  if (selectedProject && !showForm) {
    return (
      <ProjectDetail
        project={selectedProject}
        houseId={houseId}
        onClose={handleCloseDetail}
        onEdit={() => handleEditProject(selectedProject)}
        onDelete={() => handleDeleteProject(selectedProject.id)}
      />
    );
  }

  // Show project form
  if (showForm) {
    return (
      <ProjectForm
        project={selectedProject}
        houseId={houseId}
        onClose={handleCloseForm}
        onSave={loadProjects}
      />
    );
  }

  // Show project list
  const filteredProjects = projects.filter(p => {
    if (!filterStatus) return true;
    // Handle both string and enum status values
    const projectStatus = typeof p.status === 'string' ? p.status : (p.status?.value || 'planned');
    return projectStatus === filterStatus;
  });

  return (
    <div className="projects-tab">
      <div className="projects-header">
        <div>
          <h2>🏗️ Home Improvements Projects</h2>
          <p className="projects-subtitle">Manage your home improvement projects, track budgets, and organize ideas</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              ⊞
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              ☰
            </button>
          </div>
          <button className="btn-add" onClick={handleCreateProject}>
            + New Project
          </button>
        </div>
      </div>

      <div className="projects-filters">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="projects-empty">
          <p>No projects found.</p>
          <p>Click "+ New Project" to create your first home improvement project!</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'projects-grid' : 'projects-list'}>
          {filteredProjects.map((project) => {
            const budgetProgress = project.budget_estimated
              ? calculateBudgetProgress(project.budget_estimated, project.budget_actual || 0)
              : 0;
            const statusStr = typeof project.status === 'string' ? project.status : (project.status?.value || 'planned');
            const priorityStr = typeof project.priority === 'string' ? project.priority : (project.priority?.value || 'medium');
            const statusColor = getStatusColor(statusStr);
            const priorityColor = getPriorityColor(priorityStr);

            return (
              <div
                key={project.id}
                className="project-card"
                onClick={() => handleViewProject(project)}
              >
                <div className="project-card-header">
                  <h3 className="project-name">{project.name}</h3>
                  <div className="project-badges">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: statusColor }}
                      title={getStatusDisplay(project.status)}
                    >
                      {getStatusDisplay(project.status)}
                    </span>
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: priorityColor }}
                      title={`Priority: ${getPriorityDisplay(project.priority)}`}
                    >
                      {getPriorityDisplay(project.priority)}
                    </span>
                  </div>
                </div>

                {project.description && (
                  <p className="project-description">{project.description}</p>
                )}

                {project.category && (
                  <div className="project-category">
                    <span className="category-tag">{project.category}</span>
                  </div>
                )}

                <div className="project-budget">
                  <div className="budget-info">
                    <span className="budget-label">Budget:</span>
                    <span className="budget-estimated">
                      {formatCurrency(project.budget_estimated)}
                    </span>
                    {project.budget_actual > 0 && (
                      <span className="budget-actual">
                        Spent: {formatCurrency(project.budget_actual)}
                      </span>
                    )}
                  </div>
                  {project.budget_estimated && project.budget_estimated > 0 && (
                    <div className="budget-progress-bar">
                      <div
                        className="budget-progress-fill"
                        style={{
                          width: `${budgetProgress}%`,
                          backgroundColor: budgetProgress > 100 ? 'var(--error)' : 'var(--primary)',
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="project-dates">
                  {project.start_date && (
                    <div className="date-item">
                      <span className="date-label">Start:</span>
                      <span className="date-value">{formatDate(project.start_date)}</span>
                    </div>
                  )}
                  {project.target_date && (
                    <div className="date-item">
                      <span className="date-label">Target:</span>
                      <span className="date-value">{formatDate(project.target_date)}</span>
                    </div>
                  )}
                  {project.completion_date && (
                    <div className="date-item">
                      <span className="date-label">Completed:</span>
                      <span className="date-value">{formatDate(project.completion_date)}</span>
                    </div>
                  )}
                </div>

                <div className="project-card-actions">
                  <button
                    className="btn-view"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewProject(project);
                    }}
                  >
                    View Details
                  </button>
                  <button
                    className="btn-edit-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditProject(project);
                    }}
                    title="Edit"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button
                    className="btn-delete-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteProject(project.id);
                    }}
                    title="Delete"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectsTab;
